import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Stack,
  Button,
  Typography,
  CircularProgress,
  Popper,
} from "@mui/material";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import "./style.css";
import EmojiPicker from "emoji-picker-react";
import { ReactComponent as SmileEmoji } from "../../../../assets/images/ChatImages/smile.svg";
import { ReactComponent as FileSharing } from "../../../../assets/images/ChatImages/filesharing.svg";
import { ReactComponent as Hash } from "../../../../assets/images/ChatImages/hash.svg";
import { ReactComponent as Down01 } from "../../../../assets/images/TicketImages/down01.svg";
import { ReactComponent as Public } from "../../../../assets/images/TicketImages/share.svg";
import { ReactComponent as Note } from "../../../../assets/images/TicketImages/note.svg";
import { agentReplyToTicket } from "../../../../store/features/agentReplyToTicket";
import { getTicketMessagesById } from "../../../../store/features/getTicketMessagesById";
import { updateTicketData } from "../../../../store/features/getTicketById";
import { updateTicketOptimistic } from "../../../../store/features/getAllTickets";
import SubmitMenu from "./SubmitMenu";
import ListTags from "../../ChatComponents/ListTags";
import CannedPopup from "../../ChatComponents/CannedPopup";
import { useSocket } from "../../../../SocketContext";
import { fetchCannedMessages } from "../../../../store/features/cannedMessagesApi";

const TV2_TEXTAREA_MIN_HEIGHT = 48;
const TV2_TEXTAREA_MAX_HEIGHT = 160;

function TicketV2CannedList({
  items,
  loading,
  error,
  onSelect,
}) {
  return (
    <Box
      sx={{
        width: 320,
        maxHeight: 300,
        overflowY: "auto",
        border: "1px solid #D5DCE1",
        borderRadius: "8px",
        bgcolor: "#FFF",
        boxShadow: "0 8px 24px rgba(6, 21, 34, 0.14)",
        p: 1,
        boxSizing: "border-box",
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Typography sx={{ px: 1, py: 0.75, fontSize: 13, fontWeight: 600, color: "#061522" }}>
        Canned responses
      </Typography>
      <Box>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : error ? (
          <Typography sx={{ px: 1, py: 2, fontSize: 13, color: "#D32F2F" }}>
            Could not load canned responses.
          </Typography>
        ) : items.length === 0 ? (
          <Typography sx={{ px: 1, py: 2, fontSize: 13, color: "#8B97A2" }}>
            No canned responses found.
          </Typography>
        ) : (
          items.map((item) => (
            <Box
              key={item._id || item.id || item.shortcut}
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item);
              }}
              sx={{
                display: "block",
                width: "100%",
                border: 0,
                borderRadius: "6px",
                bgcolor: "transparent",
                textAlign: "left",
                px: 1,
                py: 1,
                cursor: "pointer",
                "&:hover": { bgcolor: "#F5F8FA" },
              }}
            >
              <Stack direction="row" justifyContent="space-between" gap={1}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#061522" }}>
                  {item.title || item.shortcut || "Untitled"}
                </Typography>
                {item.shortcut ? (
                  <Typography sx={{ fontSize: 11, color: "#667785", flexShrink: 0 }}>
                    {item.shortcut}
                  </Typography>
                ) : null}
              </Stack>
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: 12,
                  color: "#667785",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.content || ""}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

function TicketMessageInput({
  ticketId,
  setSelectedState,
  setActiveTicketMerge,
  messageContainerRef,
  isDisabled,
  socketInstance,
  ticket,
  ticketStatus,
  selectedGroupId,
  /** "tv2" = Figma-style tabs + framed composer (TicketV2). */
  variant = "default",
}) {
  const disabledPlaceholder =
    ticketStatus === "spam"
      ? "This ticket is marked as spam. No further messages can be sent."
      : "This ticket is solved. No further messages can be sent.";
  const dispatch = useDispatch();
  const socket = useSocket();
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [inputText, setInputText] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [publicNote, setPublicNote] = useState(false);
  const [submitNote, setSubmitNote] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filePreviewUrls, setFilePreviewUrls] = useState([]);
  const [isPrivate, setIsPrivate] = useState("false");
  const [tagMenu, setTagMenu] = useState(false);
  const [cannedResponse, setCannedResponse] = useState(false);
  const [cannedMsg, setCannedMsg] = useState("");
  const { loading, error } = useSelector((state) => state.agentReply);
  const dropdownRef = useRef();
  const cannedMenuRef = useRef(null);
  const menuRef = useRef(null);
  const emojiAnchorRef = useRef(null);
  const emojiPickerRef = useRef(null);

  const MAX_HEIGHT = 200; // Maximum height in pixels before scrolling

  // Auto-resize textarea function
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    const scrollHeight = textarea.scrollHeight;
    const minHeight = variant === "tv2" ? TV2_TEXTAREA_MIN_HEIGHT : 0;
    const maxHeight = variant === "tv2" ? TV2_TEXTAREA_MAX_HEIGHT : MAX_HEIGHT;
    const nextHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

    textarea.style.height = `${nextHeight}px`;
    if (scrollHeight <= maxHeight) {
      textarea.style.overflowY = 'hidden';
    } else {
      textarea.style.overflowY = 'auto';
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setPublicNote(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const userData = useSelector((state) => state.loginToken);
  const {
    list: cannedMessages = [],
    loading: cannedMessagesLoading,
    error: cannedMessagesError,
  } = useSelector((state) => state.cannedMessages);
  const businessId = userData?.response?.user?.business_id;
  const currentUserId = userData?.response?.user?._id;

  const ticketGroupId =
    ticket?.groups?.[0]?._id ||
    ticket?.groups?.[0]?.id ||
    ticket?.group?._id ||
    ticket?.group?.id ||
    "";
  const resolvedCannedGroupId = selectedGroupId || ticketGroupId;

  /** Only the assigned agent may send a public reply; others are limited to internal notes. Unassigned tickets stay open for public (first reply auto-assigns). */
  const canSendPublicReply = useMemo(() => {
    const assignee = ticket?.assignee;
    if (assignee == null || assignee === "") return true;
    const assigneeId =
      typeof assignee === "object"
        ? assignee._id ?? assignee.id ?? null
        : assignee;
    if (!assigneeId) return true;
    return (
      currentUserId != null && String(assigneeId) === String(currentUserId)
    );
  }, [ticket?.assignee, currentUserId]);

  useEffect(() => {
    if (
      variant !== "tv2" ||
      !tagMenu ||
      !businessId ||
      !resolvedCannedGroupId
    ) return;
    dispatch(
      fetchCannedMessages({
        businessId,
        groupId: resolvedCannedGroupId,
      })
    );
  }, [variant, tagMenu, businessId, resolvedCannedGroupId, dispatch]);

  useEffect(() => {
    if (!canSendPublicReply) {
      setIsPrivate("true");
    }
  }, [canSendPublicReply, ticketId]);

  useEffect(() => {
    if (isPrivate !== "true") return;
    setTagMenu(false);
    setCannedResponse(false);
  }, [isPrivate]);

  const handleSendMessage = async () => {
    const trimmedInput = inputText.trim();

    if (!trimmedInput) {
      toast.error("Please enter a message");
      return;
    }

    setIsSubmitting(true);

    const messageToSend = trimmedInput;
    const attachmentsToSend = [...attachments];
    const previewUrlsToSend = [...filePreviewUrls];
    const effectiveIsPrivate = canSendPublicReply ? isPrivate : "true";

    // Clear input and attachments immediately
    setInputText("");
    setAttachments([]);
    setFilePreviewUrls([]);
    setShowEmojiPicker(false);

    // Reset textarea height after clearing.
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.overflowY = "hidden";
    }

    const optimisticMessage = {
      type: "agent",
      message: messageToSend,
      sender: userData?.response?.user?.full_name,
      created_at: new Date().toISOString(),
      _id: Date.now().toString(),
      attachments: [],
      isAdmin: userData?.response?.user?.role_name === 'admin',
      agent: {
        ...userData?.response?.user,
        agent_image: userData?.response?.user?.agent_image
      },
      isPrivate: effectiveIsPrivate === "true"
    };

    if (attachmentsToSend.length > 0) {
      attachmentsToSend.forEach((file, index) => {
        if (file.type.startsWith("image/")) {
          optimisticMessage.attachments.push({
            filename: file.name,
            url: previewUrlsToSend[index],
            mimeType: file.type,
            _id: `temp-${Date.now()}-${index}`,
          });
        }
      });
    }

    if (socketInstance) {
      socketInstance.emit("agent-reply", {
        ticketId,
        message: messageToSend,
        senderId: userData?.response?.user?._id,
        isPrivate: effectiveIsPrivate === "true",
        isAdmin: userData?.response?.user?.role_name === 'admin',
        attachments: optimisticMessage.attachments
      });
    }

    dispatch({
      type: "getTicketMessagesById/optimisticAdd",
      payload: optimisticMessage,
    });

    if (messageContainerRef.current) {
      setTimeout(() => {
        messageContainerRef.current.scrollTop =
          messageContainerRef.current.scrollHeight;
      }, 0);
    }

    try {
      await dispatch(
        agentReplyToTicket({
          ticketId,
          message: messageToSend,
          attachments: attachmentsToSend,
          isPrivate: effectiveIsPrivate,
        })
      ).unwrap();

      // Emit socket event for status change (open -> pending) when agent replies
      if (socket && ticketStatus === 'open' && effectiveIsPrivate !== "true") {
        const newStatus = 'pending';
        socket.emit('ticket-status-changed', {
          ticketId,
          oldStatus: ticketStatus,
          newStatus,
          agentId: userData?.response?.user?._id,
          businessId: userData?.response?.user?.business_id,
          ticket: {
            ...ticket,
            status: newStatus
          }
        });
      }

      await dispatch(getTicketMessagesById({ ticketId, silent: true }));

      // First public reply on an unassigned ticket auto-assigns the agent.
      // Update details locally — do not refetch ticket details on every reply.
      const wasUnassigned =
        effectiveIsPrivate !== "true" &&
        !ticket?.assignee?._id &&
        !ticket?.assignee?.id &&
        (ticket?.assignee == null || ticket?.assignee === "");
      if (wasUnassigned) {
        const me = userData?.response?.user;
        if (me?._id) {
          const assignee = {
            _id: me._id,
            full_name: me.full_name,
            email: me.email,
            agent_image: me.agent_image,
          };
          dispatch(updateTicketData({ assignee }));
          dispatch(
            updateTicketOptimistic({
              _id: ticketId,
              assignee,
            })
          );
        }
      }
    } catch (error) {
      let errorMessage = "Failed to send message";

      if (error instanceof Error) {
        errorMessage = error.message;
      }
      else if (error && typeof error === 'object') {
        if (error.message) {
          errorMessage = error.message;
        } else if (error.data && error.data.message) {
          errorMessage = error.data.message;
        }
      }

      toast.error(errorMessage);

      dispatch({
        type: "getTicketMessagesById/optimisticRemove",
        payload: optimisticMessage._id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    const hashRegex = /(^|\s)#$/;

    if (isPrivate === "true" || !canSendPublicReply) {
      setTagMenu(false);
    } else if (hashRegex.test(value)) {
      setTagMenu(true);
    } else if (value.includes("#")) {
      setTagMenu(false);
    } else {
      setTagMenu(false);
    }

    setInputText(value);
    setCannedMsg(value);
  };

  const handleMessageSelect = (message) => {
    const content = message?.content || message?.message || "";
    setInputText((prevText) => {
      const lastHashIndex = prevText.lastIndexOf('#');

      if (lastHashIndex !== -1) {
        return `${prevText.slice(0, lastHashIndex)}${content}${prevText.slice(lastHashIndex + 1)}`;
      }

      return `${prevText}${content}`;
    });

    setTagMenu(false);
    // Parent hash trigger may still receive the same click and reopen the menu.
    setTimeout(() => setTagMenu(false), 0);

    setTimeout(() => {
      const inputElement = textareaRef.current;
      if (inputElement) {
        inputElement.focus();
      }
      adjustTextareaHeight();
    }, 0);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (variant === "tv2" && (!inputText.trim() || isDisabled || isSubmitting)) {
        return;
      }
      handleSendMessage();
    }
  };

  const handleToggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleEmojiClick = (emojiObject) => {
    setInputText((prevText) => prevText + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length !== files.length) {
      toast.warn("Only image files are allowed");
    }

    setAttachments((prevAttachments) => [...prevAttachments, ...imageFiles]);

    const newPreviewUrls = imageFiles.map((file) => URL.createObjectURL(file));
    setFilePreviewUrls((prevUrls) => [...prevUrls, ...newPreviewUrls]);
  };

  const removeAttachment = (index) => {
    setAttachments((prevAttachments) =>
      prevAttachments.filter((_, i) => i !== index)
    );

    if (filePreviewUrls[index]) {
      URL.revokeObjectURL(filePreviewUrls[index]);
    }
    setFilePreviewUrls((prevUrls) => prevUrls.filter((_, i) => i !== index));
  };

  const handleClickOutside = (event) => {
    const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);

    const isOutsideCannedMenu =
      !cannedMenuRef.current || !cannedMenuRef.current.contains(event.target);
    if (isOutsideDropdown && isOutsideCannedMenu) {
      setTagMenu(false);
    }

    const isOutsideEmoji =
      emojiAnchorRef.current &&
      !emojiAnchorRef.current.contains(event.target) &&
      (!emojiPickerRef.current || !emojiPickerRef.current.contains(event.target));
    if (isOutsideEmoji) {
      setShowEmojiPicker(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      filePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [filePreviewUrls]);

  // Grow with content until the composer reaches its scrolling threshold.
  useEffect(() => {
    requestAnimationFrame(() => {
      adjustTextareaHeight();
    });
  }, [inputText, variant]);

  const attachmentBlock =
    attachments.length > 0 ? (
      <div className="attachments-preview">
        {attachments.map((file, index) => (
          <div key={index} className="attachment-item">
            <div className="image-preview-container">
              <img
                src={filePreviewUrls[index]}
                alt="Preview"
                className="image-preview"
              />
            </div>
            <span className="attachment-filename">{file.name}</span>
            <button
              type="button"
              onClick={() => removeAttachment(index)}
              className="remove-attachment-btn"
              aria-label="Remove attachment"
            >
              <span style={{ marginBottom: "2px" }}>×</span>
            </button>
          </div>
        ))}
      </div>
    ) : null;

  const fileInputEl = (
    <input
      type="file"
      ref={fileInputRef}
      onChange={handleFileChange}
      style={{ display: "none" }}
      multiple
      accept="image/*"
    />
  );

  const emojiBlock = (
    <div className="EmojiPicker-Top">
      {showEmojiPicker && (
        <EmojiPicker
          onEmojiClick={handleEmojiClick}
          disableAutoFocus
          native
        />
      )}
    </div>
  );

  if (variant === "tv2") {
    const isPublic = isPrivate === "false" && canSendPublicReply;
    const canSend = !isDisabled && !isSubmitting && inputText.trim().length > 0;
    const publicTabDisabled = isDisabled || !canSendPublicReply;

    const isInternal = !isPublic;

    return (
      <>
        <Box
          className={isDisabled ? "disabled" : ""}
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            height: "auto",
            minHeight: 160,
            boxSizing: "border-box",
            border: "1px solid #D5DCE1",
            borderRadius: "8px",
            bgcolor: isInternal ? "#FFFBE6" : "#FFF",
            boxShadow: isInternal
              ? "0 1px 2px 0 rgba(6, 21, 34, 0.05)"
              : "none",
            overflow: "visible",
            flexShrink: 0,
            fontFamily: '"Inter", sans-serif',
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Stack
            direction="row"
            sx={{
              flexShrink: 0,
              px: 2,
              gap: 3,
              justifyContent: "flex-start",
              alignItems: "stretch",
              bgcolor: isInternal ? "#FFF1B8" : "#FFF",
              borderRadius: "7px 7px 0 0",
            }}
          >
            <Box
              component="button"
              type="button"
              disabled={publicTabDisabled}
              title={
                canSendPublicReply
                  ? undefined
                  : "Only the assigned agent can send a public reply"
              }
              onClick={() =>
                !publicTabDisabled && setIsPrivate("false")
              }
              sx={{
                flex: "0 0 auto",
                py: 1.25,
                px: 0,
                border: 0,
                bgcolor: "transparent",
                cursor: publicTabDisabled ? "not-allowed" : "pointer",
                opacity: !canSendPublicReply ? 0.45 : 1,
                borderBottom: isPublic ? "2px solid #148DFD" : "2px solid transparent",
                mb: "-1px",
                textAlign: "left",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: isPublic ? 600 : 400,
                  color: isPublic ? "#061522" : "#8B97A2",
                  fontFamily: "inherit",
                }}
              >
                Public Reply
              </Typography>
            </Box>
            <Box
              component="button"
              type="button"
              disabled={isDisabled}
              onClick={() => !isDisabled && setIsPrivate("true")}
              sx={{
                flex: "0 0 auto",
                py: 1.25,
                px: 0,
                border: 0,
                bgcolor: "transparent",
                cursor: isDisabled ? "not-allowed" : "pointer",
                borderBottom: isInternal ? "2px solid #148DFD" : "2px solid transparent",
                mb: "-1px",
                textAlign: "left",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: isInternal ? 600 : 400,
                  color: isInternal ? "#061522" : "#8B97A2",
                  fontFamily: "inherit",
                }}
              >
                Internal Note
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              flexShrink: 0,
              height: "1px",
              bgcolor: isInternal ? "#F3E1A6" : "#E5E9EC",
              mx: 2,
            }}
          />

          <Box
            sx={{
              flex: "0 0 auto",
              minHeight: TV2_TEXTAREA_MIN_HEIGHT,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              px: 2,
              pt: 1.5,
              bgcolor: isInternal ? "#FFFBE6" : "transparent",
            }}
          >
            {attachmentBlock}
            <textarea
              ref={textareaRef}
              placeholder={
                isDisabled
                  ? disabledPlaceholder
                  : "Type your message ..."
              }
              value={inputText}
              onKeyDown={handleKeyPress}
              onChange={handleInputChange}
              disabled={isDisabled}
              style={{
                flex: "0 0 auto",
                height: TV2_TEXTAREA_MIN_HEIGHT,
                minHeight: TV2_TEXTAREA_MIN_HEIGHT,
                maxHeight: TV2_TEXTAREA_MAX_HEIGHT,
                width: "100%",
                resize: "none",
                border: 0,
                outline: "none",
                fontSize: 14,
                fontFamily: "Inter, sans-serif",
                color: "#061522",
                overflowY: "hidden",
                background: "transparent",
              }}
            />
          </Box>

          {fileInputEl}

          <Box
            sx={{
              flexShrink: 0,
              height: "1px",
              bgcolor: "#F1F3F5",
              mx: 2,
            }}
          />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.25,
              borderRadius: "0 0 7px 7px",
              bgcolor: isInternal ? "#FFFBE6" : "transparent",
              position: "relative",
              overflow: "visible",
              zIndex: 2,
            }}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Box
                ref={emojiAnchorRef}
                sx={{ position: "relative", display: "inline-flex", alignItems: "center" }}
              >
                <Box
                  component="span"
                  onClick={!isDisabled ? handleToggleEmojiPicker : undefined}
                  sx={{
                    display: "inline-flex",
                    p: 0.75,
                    cursor: isDisabled ? "default" : "pointer",
                    opacity: isDisabled ? 0.4 : 1,
                    color: "#405261",
                  }}
                >
                  <SmileEmoji />
                </Box>
                <Popper
                  open={showEmojiPicker}
                  anchorEl={emojiAnchorRef.current}
                  placement="top-start"
                  sx={{ zIndex: 1500 }}
                  modifiers={[
                    { name: "offset", options: { offset: [0, 8] } },
                    { name: "preventOverflow", options: { padding: 8 } },
                  ]}
                >
                  <Box
                    ref={emojiPickerRef}
                    sx={{
                      boxShadow: "0 8px 24px rgba(6, 21, 34, 0.12)",
                      borderRadius: "8px",
                      overflow: "hidden",
                    }}
                  >
                    <EmojiPicker
                      onEmojiClick={handleEmojiClick}
                      disableAutoFocus
                      native
                    />
                  </Box>
                </Popper>
              </Box>
              <Box
                component="span"
                onClick={!isDisabled ? handleFileSelect : undefined}
                sx={{
                  display: "inline-flex",
                  p: 0.75,
                  cursor: isDisabled ? "default" : "pointer",
                  opacity: isDisabled ? 0.4 : 1,
                  color: "#405261",
                }}
              >
                <FileSharing />
              </Box>
              {!isInternal && (
              <Box
                ref={dropdownRef}
                onClick={
                  !isDisabled
                    ? (e) => {
                        if (cannedMenuRef.current?.contains(e.target)) return;
                        setTagMenu((open) => !open);
                      }
                    : undefined
                }
                sx={{
                  display: "inline-flex",
                  p: 0.75,
                  cursor: isDisabled ? "default" : "pointer",
                  opacity: isDisabled ? 0.4 : 1,
                  color: "#405261",
                  position: "relative",
                }}
              >
                <Hash />
                <Popper
                  open={tagMenu}
                  anchorEl={dropdownRef.current}
                  placement="top-start"
                  sx={{ zIndex: 1501 }}
                  modifiers={[
                    { name: "offset", options: { offset: [0, 8] } },
                    { name: "preventOverflow", options: { padding: 8 } },
                  ]}
                >
                  <Box
                    ref={cannedMenuRef}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <TicketV2CannedList
                      items={cannedMessages}
                      loading={cannedMessagesLoading}
                      error={cannedMessagesError}
                      onSelect={handleMessageSelect}
                    />
                  </Box>
                </Popper>
              </Box>
              )}
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  p: 0.75,
                  color: "#C5CED6",
                  cursor: "not-allowed",
                }}
                title="Formatting"
              >
                <FormatSizeIcon sx={{ fontSize: 20 }} />
              </Box>
            </Stack>
            <Button
              variant="contained"
              size="medium"
              disableElevation
              disabled={!canSend}
              onClick={handleSendMessage}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: 14,
                lineHeight: "18px",
                borderRadius: "8px",
                width: 128,
                minWidth: 128,
                maxWidth: 128,
                height: 40,
                px: 0,
                boxSizing: "border-box",
                bgcolor: canSend ? "#148DFD" : "#FFF",
                color: canSend ? "#FFF" : "#93A1B0",
                border: canSend ? "1px solid #148DFD" : "1px solid #D5DCE1",
                boxShadow: canSend
                  ? "none"
                  : "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                "&:hover": {
                  bgcolor: canSend ? "#0f7ad4" : "#FFF",
                  border: canSend ? "1px solid #0f7ad4" : "1px solid #D5DCE1",
                  boxShadow: canSend
                    ? "none"
                    : "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                },
                "&.Mui-disabled": {
                  bgcolor: "#FFF",
                  color: "#93A1B0",
                  border: "1px solid #D5DCE1",
                },
              }}
            >
              Send Message
            </Button>
          </Stack>
        </Box>
        {cannedResponse && !isInternal && (
          <CannedPopup
            cannedMsg={cannedMsg}
            setCannedResponse={setCannedResponse}
            isTicketCannedMessage={true}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className={`ChatMessageInput-Main-Container ${isDisabled ? 'disabled' : ''}`}>
        <div className="ChatMessageInput-Container">
          <textarea
            ref={textareaRef}
            placeholder={isDisabled ? disabledPlaceholder : "Type your message ..."}
            value={inputText}
            onKeyDown={handleKeyPress}
            onChange={handleInputChange}
            className="ChatInput"
            disabled={isDisabled}
            style={{
              minHeight: '46px',
              maxHeight: `${MAX_HEIGHT}px`,
              resize: 'none',
              overflowY: inputText ? 'auto' : 'hidden',
              transition: 'height 0.15s ease'
            }}
          />
        </div>

        {attachmentBlock}

        {emojiBlock}

        {fileInputEl}

        <div className="ChatMessage-Button-Container">
          <div className="ChatMessage-Emoji00">
            <p onClick={!isDisabled ? handleToggleEmojiPicker : undefined} className={isDisabled ? 'disabled' : ''}>
              <SmileEmoji />
            </p>
            <p onClick={!isDisabled ? handleFileSelect : undefined} className={isDisabled ? 'disabled' : ''}>
              <FileSharing />
            </p>
            {isPrivate !== "true" && (
            <p
              ref={dropdownRef}
              onClick={
                !isDisabled
                  ? (e) => {
                      // ListTags is nested under this trigger; ignore bubbled item clicks.
                      if (e.target !== e.currentTarget && e.target.closest?.(".dropdown-canned-tag")) {
                        return;
                      }
                      setTagMenu((open) => !open);
                    }
                  : undefined
              }
              className={isDisabled ? 'disabled' : ''}
            >
              <Hash />
              {tagMenu && <ListTags setCannedResponse={setCannedResponse} onMessageSelect={handleMessageSelect} />}
            </p>
            )}
          </div>
          <div className="TicketButton-Main-Container">
            <button
              type="button"
              className="TicketInputButton02"
              onClick={() => !isDisabled && setPublicNote(!publicNote)}
              disabled={isDisabled || isSubmitting}


            >
              {isPrivate === "false" && canSendPublicReply
                ? "Public Reply"
                : "Internal Note"}
              <span className="public-icon-container">
                <Down01 />
              </span>
            </button>
            {publicNote && (
              <div ref={menuRef} className="PublicMenu-Container">

                <div
                  className="PublicMenu-Main"
                  style={
                    !canSendPublicReply
                      ? { opacity: 0.45, cursor: "not-allowed" }
                      : undefined
                  }
                  title={
                    canSendPublicReply
                      ? undefined
                      : "Only the assigned agent can send a public reply"
                  }
                >
                  <Public />
                  <p
                    onClick={() => {
                      if (!canSendPublicReply) return;
                      setIsPrivate("false");
                      setPublicNote(false);
                    }}
                  >
                    Public Message
                  </p>
                </div>
                <div
                  className="PublicMenu-Main"
                  onClick={() => {
                    setPublicNote(false);
                    setIsPrivate("true");
                  }}
                >
                  <Note />
                  <p>Internal Note</p>
                </div>
              </div>
            )}
            <button className="TicketInputButton01" onClick={handleSendMessage} disabled={isDisabled}>
              {"Send Message"}
            </button>
            {submitNote && <SubmitMenu />}
          </div>
        </div>
      </div>
      {cannedResponse && isPrivate !== "true" && (
        <CannedPopup cannedMsg={cannedMsg} setCannedResponse={setCannedResponse} isTicketCannedMessage={true} />
      )}
    </>
  );
}

export default TicketMessageInput;